import React, { Fragment, useEffect, useState } from "react";
import { Button, Table, Modal, Form, FormLabel } from "react-bootstrap";
import { Breadcrumbs, H5, P, ToolTip } from "../../AbstractElements";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, Col, Input, Label } from "reactstrap";
import { Filter, PlusCircle, X } from "react-feather";
import MyPagination from "../pagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLoader } from "../../_helper/LoaderContext";
import { deleteItem, exportPharmaItemsAPI, fetchAllPharmacyItems, getItemsList, importPharmaItemsAPI } from "./PharmacyMedicineService";
import Swal from "sweetalert2";
import { FaDownload, FaPlusCircle, FaUpload } from "react-icons/fa";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { useAtom } from "jotai";
import { MedicinePageAtom, MedicinePageSizeAtom } from "./MedicineAtoms";
import { useCurrency } from "../../_helper/CurrencyContext";
import BarcodePrintModal from "../Common/Barcode/BarcodePrintModal";
import excelDocumentImg from "../../assets/images/excel-file.png";
import {
	buildImportEnrichmentMessage,
	enrichImportExcelBarcodes,
} from "./utils/importBarcodeEnricher";

const getExportFileUrl = (response) => {
	const data = response?.data?.data;
	if (!data) return null;
	if (typeof data === "string") return data;
	return data.downloadLink || data.url || data.fileUrl || data.excelUrl || null;
};

const ItemsList = () => {
	const { t } = useTranslation();
	const { getCurrencySymbol } = useCurrency();

	const [searchValue, setSearchValue] = useState('');
	const [printBarcodeItem, setPrintBarcodeItem] = useState(null);
	const [totalItems, setTotalItems] = useState(0);
	const [page, setPage] = useAtom(MedicinePageAtom);
	const [size, setSize] = useAtom(MedicinePageSizeAtom);
	const [isClearing, setIsClearing] = useState(false);
	const history = useNavigate();
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const { pharmacyPOS } = usePharmacy();
	const [itemType, setItemType] = useState("medicine"); // "medicine" | "otc" | "unani"

	const [editTooltipOpen, setEditTooltipOpen] = useState(false);
	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
	const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
	const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);
	const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
	const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);

	const [brandTooltipOpen, setBrandTooltipOpen] = useState(false);
	const [saltTooltipOpen, setSaltTooltipOpen] = useState(false);
	const [manuTooltipOpen, setManuTooltipOpen] = useState(false);
	const toggleBrandTooltip = () => setBrandTooltipOpen(!brandTooltipOpen);
	const toggleSaltTooltip = () => setSaltTooltipOpen(!saltTooltipOpen);
	const toggleManuTooltip = () => setManuTooltipOpen(!manuTooltipOpen);

	const [items, setItems] = useState([]);

	useEffect(() => {
		getItems();
	}, [page, size, itemType]);

	const getItems = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			page: page,
			limit: size,
			search: searchValue.trim(),
			isOTC: itemType === "otc",
			isUnani: itemType === "unani",
		};
		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getItemsList(body);
			if (response.data.status === 'success') {
				setItems(response.data.data.items || []);
				const totalCount = response.data.data.total || 0;
				setTotalItems(Math.ceil(totalCount / size));
			} else {
				setItems([])
				setTotalItems(0)
			}
		} catch (error) {
			console.error("Error fetching items:", error);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const [showImportModal, setShowImportModal] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [importFileName, setImportFileName] = useState("");
	const [importFileError, setImportFileError] = useState("");
	const [isExporting, setIsExporting] = useState(false);

	const resetImportForm = () => {
		setSelectedFile(null);
		setImportFileName("");
		setImportFileError("");
	};

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const isValidType =
			file.name.toLowerCase().endsWith(".xlsx") ||
			file.name.toLowerCase().endsWith(".xls");

		if (!isValidType) {
			setImportFileError(t("Only Excel files (.xls, .xlsx) are allowed."));
			setSelectedFile(null);
			setImportFileName("");
			e.target.value = "";
			return;
		}

		setImportFileError("");
		setSelectedFile(file);
		setImportFileName(file.name);
	};

	const handleUpload = async () => {
		if (!selectedFile) {
			setImportFileError(t("Please select a file before uploading."));
			return;
		}

		try {
			setLoading(true);

			const clinicId = pharmacyPOS?.hospital_id;
			const [medicineItems, otcItems] = await Promise.all([
				fetchAllPharmacyItems({ clinicId, isOTC: false }),
				fetchAllPharmacyItems({ clinicId, isOTC: true }),
			]);

			const { file: enrichedFile, summary, errors } = await enrichImportExcelBarcodes({
				file: selectedFile,
				clinicId,
				existingItems: [...medicineItems, ...otcItems],
			});

			if (errors?.length) {
				setImportFileError(errors[0]);
				toast.error(errors[0]);
				return;
			}

			const enrichmentMessage = buildImportEnrichmentMessage(summary, t);
			if (enrichmentMessage) {
				toast.info(enrichmentMessage);
			}

			const formData = new FormData();
			formData.append("file", enrichedFile);

			const response = await importPharmaItemsAPI(formData);

			if (response.data?.status === "success") {
				toast.success(response.data.message || t("Items imported successfully"));
				resetImportForm();
				getItems();
				setShowImportModal(false);
			} else {
				toast.warning(response.data?.message || t("File upload failed."));
			}
		} catch (error) {
			console.error(error);
			toast.error(
				error?.message || t("Failed to prepare import file. Please try again.")
			);
		} finally {
			setLoading(false);
		}
	};

	const handleSearchButtonClick = () => {
		if (searchValue.trim !== "") {
			setPage(1);
			getItems();
		}
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue('');
	};

	useEffect(() => {
		if (isClearing) {
			getItems();
			setIsClearing(false);
		}
	}, [isClearing]);

	const handleSearchChange = (event) => {
		setSearchValue(event.target.value);
	};

	const handlePageChange = (pageNumber) => {
		setPage(pageNumber);
	};

	const handlePageSizeChange = (e) => {
		const newSize = parseFloat(e.target.value);
		setSize(newSize);
		setPage(1);
	};

	const openImportModal = () => {
		setShowImportModal(true);
	};

	const closeImportModal = () => {
		setShowImportModal(false);
		resetImportForm();
	};

	// Event handler for opening add component
	const addItem = () => {
		history(`${process.env.PUBLIC_URL}/add-item`);
		// if (itemType === "medicine") {
		// } else {
		// 	history(`${process.env.PUBLIC_URL}/add-OTC`);
		// }
	};

	// Event handler for opening edit component
	const handleEditClick = (getID) => {
		if (itemType === "otc") {
			history(`${process.env.PUBLIC_URL}/update-OTC/${getID}`);
		} else {
			history(`${process.env.PUBLIC_URL}/update-medicine/${getID}`);
		}
	};

	// Event handler for opening view component
	const handleViewClick = (getID) => {
		if (itemType === "otc") {
			history(`${process.env.PUBLIC_URL}/view-OTC/${getID}`);
		} else {
			history(`${process.env.PUBLIC_URL}/view-medicine/${getID}`);
		}
	};

	const handleExportItems = async () => {
		try {
			setIsExporting(true);
			setLoading(true);

			const response = await exportPharmaItemsAPI({
				search: searchValue.trim(),
			});

			if (response?.data?.status === "success") {
				const fileUrl = getExportFileUrl(response);

				if (fileUrl) {
					toast.success(
						response.data.message || t("Items exported successfully")
					);

					const link = document.createElement("a");
					link.href = fileUrl;
					link.download = "";
					link.target = "_blank";
					link.rel = "noopener noreferrer";

					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);

					// const fileKey = response.data.data?.fileKey;
					// const fileName = fileKey
					// 	? fileKey.split("/").pop()
					// 	: fileUrl.split("?")[0].split("/").pop();

					// const link = document.createElement("a");
					// link.href = fileUrl;
					// link.download = fileName || "";
					// link.target = "_blank";
					// link.rel = "noopener noreferrer";

					// document.body.appendChild(link);
					// link.click();
					// document.body.removeChild(link);
				} else {
					toast.warning(t("Export file URL not found in response."));
				}
			}
		} catch (error) {
			console.error("Error exporting items:", error);
		} finally {
			setLoading(false);
			setIsExporting(false);
		}
	};

	const handleDelete = async (id) => {
		const confirm = await Swal.fire({
			title: t("Are you sure?"),
			text: t("You want to delete this item"),
			icon: "warning",
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn-primary',
				cancelButton: 'btn-danger',
			},
			confirmButtonText: t("Yes, Delete It!"),
		});

		if (confirm.isConfirmed) {
			try {
				setLoading(true);
				const response = await deleteItem(id);
				if (response?.data?.status === "success") {
					toast.success(response.data.message);
					getItems();
				}
				setLoading(false);
			} catch (err) {
				setLoading(false);
				console.error("Delete error:", err);
			}
		}
	};

	// Helper function to get table headers based on item type
	const getTableHeaders = () => {
		if (itemType === "otc") {
			return (
				<tr>
					<th>{t('Product Name')}</th>
					<th>{t('Manufacturer Name')}</th>
					<th>{t('Product Form')}</th>
					<th>{t("Packaging Type")}</th>
					<th>{t('Quantity')}</th>
					<th>{t('Price')}</th>
					<th className="text-center">{t("Action")}</th>
				</tr>
			);
		}
		return (
			<tr>
				<th>{t('Brand Name')}</th>
				<th>{t('Salt Name / Composition')}</th>
				<th>{t('Manufacturer Name')}</th>
				<th>{t('Product Form')}</th>
				<th>{t("Packaging Type")}</th>
				<th>{t('Product Quantity')}</th>
				<th>{t('Price')}</th>
				<th className="text-center">{t("Action")}</th>
			</tr>
		);
	};

	// Helper function to get table rows based on item type
	const getTableRows = () => {
		return items.map((item, index) => (
			<tr key={index}>
				{/* Brand/Product Name Column */}
				{itemType !== "otc" ? (
					<>
						{/* Brand Name */}
						<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							<span id={`brandToolTip-${index}`}>{item.brandName}</span>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: brandTooltipOpen === index,
									target: `brandToolTip-${index}`,
									toggle: () =>
										setBrandTooltipOpen(brandTooltipOpen === index ? null : index),
								}}
							>
								<strong>{item.brandName}</strong>
							</ToolTip>
						</td>
						{/* Salt Name / Composition */}
						<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							<span id={`saltToolTip-${index}`}>{item.saltName || '-'}</span>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: saltTooltipOpen === index,
									target: `saltToolTip-${index}`,
									toggle: () =>
										setSaltTooltipOpen(saltTooltipOpen === index ? null : index),
								}}
							>
								<strong>{item.saltName}</strong>
							</ToolTip>
						</td>

					</>
				) : (
					/* Product Name for OTC */
					<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						<span id={`brandToolTip-${index}`}>{item.brandName || '-'}</span>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: brandTooltipOpen === index,
								target: `brandToolTip-${index}`,
								toggle: () =>
									setBrandTooltipOpen(brandTooltipOpen === index ? null : index),
							}}
						>
							<strong>{item.brandName}</strong>
						</ToolTip>
					</td>
				)}

				{/* Manufacturer */}
				<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					<span id={`manuToolTip-${index}`}>{item.manufacturer || '-'}</span>
					<ToolTip
						attrToolTip={{
							placement: 'top',
							isOpen: manuTooltipOpen === index,
							target: `manuToolTip-${index}`,
							toggle: () =>
								setManuTooltipOpen(manuTooltipOpen === index ? null : index),
						}}
					>
						<strong>{item.manufacturer}</strong>
					</ToolTip>
				</td>

				<td>{item.productForm || '-'}</td>
				<td>{item.package || '-'}</td>
				<td>{item.packageQuantity || '-'}</td>
				<td>
					{item.regularPrice ? getCurrencySymbol() + item.regularPrice : '-'}
				</td>
				<td className='text-center'>
					<button className='success-action-button me-1' id="ViewToolTip" onClick={() => handleViewClick(item?._id)}>
						<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: viewTooltipOpen,
								target: 'ViewToolTip',
								toggle: toggleViewTooltip,
							}} >
							<strong>{t("View")}</strong>
						</ToolTip>
					</button>
					{item?.barcode && (
						<button
							className="success-action-button me-1"
							type="button"
							title={t("Print Barcode")}
							onClick={() => setPrintBarcodeItem(item)}
						>
							<i className="fa fa-barcode" style={{ fontSize: "18px" }} />
						</button>
					)}
					<button className='success-action-button me-1' id="EditToolTip" onClick={() => handleEditClick(item?._id)}>
						<i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: editTooltipOpen,
								target: 'EditToolTip',
								toggle: toggleEditTooltip,
							}} >
							<strong>{t("Edit")}</strong>
						</ToolTip>
					</button>
					{/* <button className='danger-action-button' id="DeleteToolTip" onClick={() => handleDelete(item?._id)}>
						<i className="fa fa-trash" style={{ fontSize: "18px" }}></i>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: deleteTooltipOpen,
								target: 'DeleteToolTip',
								toggle: toggleDeleteTooltip,
							}} >
							<strong>{t("Delete")}</strong>
						</ToolTip>
					</button> */}
				</td>
			</tr>
		));
	};

	// Helper to get the placeholder text based on item type
	const getSearchPlaceholder = () => {
		if (itemType === "otc") {
			return t("Search by product name and product form");
		}
		return t("Search by brand, salt name and product form");
	};

	const getNoItemsMessage = () => {
		if (itemType === "otc") return t("No OTC products found");
		if (itemType === "unani") return t("No Unani medicines found");
		return t("No Medicine found");
	};

	const getTitle = () => {
		if (itemType === "otc") return t("OTC");
		if (itemType === "unani") return t("U Medicine");
		return t("Medicine");
	};

	const getAddButtonText = () => {
		if (itemType === "otc") return t("Add OTC Item");
		if (itemType === "unani") return t("Add U Medicine");
		return t("Add Items");
	};

	return (
		<Fragment>
			<Breadcrumbs mainTitle={getTitle()} parent={t('Dashboard')} title={getTitle()} />
			<Col sm="12">
				<Card>
					<CardHeader className="d-flex justify-content-end align-items-center">
						<div className="d-flex flex-wrap align-items-center gap-2">
							<button
								className="btn btn-outline-primary d-flex align-items-center"
								type="button"
								onClick={handleExportItems}
								disabled={isExporting}
							>
								<FaDownload className="me-2" /> {t("Export")}
							</button>
							<button
								className="btn btn-outline-primary d-flex align-items-center"
								type="button"
								onClick={openImportModal}
							>
								<FaUpload className="me-2" /> {t("Import")}
							</button>
							<button
								className="btn btn-primary d-flex align-items-center"
								type="button"
								onClick={addItem}
							>
								<FaPlusCircle className="me-2" /> {t("Add Item")}
							</button>
						</div>
					</CardHeader>
					<div className="row" style={{ alignItems: "center", padding: "12px 24px" }}>
						<Col sm="12" lg="6" xl="6" md="6" className="align-self-end">
							<Input
								name="SearchData"
								id="SearchData"
								type="text"
								placeholder={getSearchPlaceholder()}
								value={searchValue}
								onChange={handleSearchChange}
								style={{
									lineHeight: 1.8, fontSize: "1rem !important"
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										handleSearchButtonClick();
									}
								}}
							/>
						</Col>
						<Col sm="12" lg="4" xl="4" md="4">
							<label className="form-label">{t("Item Type")}</label>
							<div className="mt-2 d-flex align-items-center flex-wrap" style={{ gap: "20px" }}>
								<div className="radio radio-primary">
									<Input
										id="itemMedicine"
										type="radio"
										name="itemType"
										value="medicine"
										checked={itemType === "medicine"}
										onChange={() => {
											setItemType("medicine");
											setPage(1);
										}}
									/>
									<Label for="itemMedicine" className='ps-3'>
										{t("Medicine")}
									</Label>
								</div>
								<div className="radio radio-primary">
									<Input
										id="itemUnani"
										type="radio"
										name="itemType"
										value="unani"
										checked={itemType === "unani"}
										onChange={() => {
											setItemType("unani");
											setPage(1);
										}}
									/>
									<Label for="itemUnani" className='ps-3' title={t("Unani Medicine")}>
										{t("U Medicine")}
									</Label>
								</div>
								<div className="radio radio-primary">
									<Input
										id="itemOtc"
										type="radio"
										name="itemType"
										value="otc"
										checked={itemType === "otc"}
										onChange={() => {
											setItemType("otc");
											setPage(1);
										}}
									/>
									<Label for="itemOtc" className='ps-3'>
										{t("OTC")}
									</Label>
								</div>
							</div>
						</Col>
						<Col className="col-xl-2 col-lg-4 col-md-4 col-sm-4 d-flex justify-content-start align-items-center" style={{ gap: '18px' }}>
							<button id='FilterToolTip' className='btn btn-primary' style={{ height: "40px" }} onClick={handleSearchButtonClick}>
								<Filter />
							</button>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: filterTooltipOpen,
									target: 'FilterToolTip',
									toggle: toggleFilterTooltip,
								}} >
								<strong>{t("Filter")}</strong>
							</ToolTip>
							<button id='ClearToolTip' className='btn btn-secondary' style={{ height: "40px" }} onClick={handleClear}>
								<X />
							</button>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: clearTooltipOpen,
									target: 'ClearToolTip',
									toggle: toggleClearTooltip,
								}} >
								<strong>{t("Clear")}</strong>
							</ToolTip>
						</Col>
					</div>
					<div className="card-block">
						<div className='row' style={{ padding: "12px 24px" }}>
							<Col sm="12" lg="12" xl="12">
								<div className="rounded table-responsive">
									<Table className='mb-0 table-center table-bordered table-hover'>
										<thead>
											{getTableHeaders()}
										</thead>
										<tbody>
											{items.length > 0 ? getTableRows() : (
												<tr>
													<td colSpan={itemType === "otc" ? "7" : "8"} className="text-center text-muted">
														{getNoItemsMessage()}
													</td>
												</tr>
											)}
										</tbody>
									</Table>
								</div>
							</Col>
							<Col sm="12" lg="12" xl="12" className="mt-4">
								<div className="text-center d-md-flex align-items-center justify-content-between">
									<div className="pt-3 show-entries">
										<FormLabel>{t("Show")}&nbsp;</FormLabel>
										<select style={{ display: 'unset', width: 'auto', padding: '0.375rem 2.25rem 0.375rem 0.75rem' }} value={size} onChange={handlePageSizeChange} className="form-control">
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={20}>20</option>
											<option value={50}>50</option>
											<option value={75}>75</option>
											<option value={100}>100</option>
										</select>&nbsp;
										<FormLabel>{t("entries")}</FormLabel>
									</div>
									<MyPagination totalPages={totalItems} currentPage={page} onPageChange={handlePageChange} />
								</div>
							</Col>
						</div>
					</div>
				</Card>
			</Col>

			<Modal show={showImportModal} onHide={closeImportModal} centered backdrop="static" keyboard={false}>
				<Modal.Header closeButton>
					<Modal.Title>{t("Import Items")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-muted small mb-3">
						{t("Upload an Excel file exported from this pharmacy to import items here.")}
					</p>
					<p className="text-muted small mb-3">
						{t("Missing barcodes for new items are generated automatically before upload. Leave barcode blank for existing items to keep their current value. Type REGENERATE to force a new barcode.")}
					</p>

					<Form.Group controlId="formFile">
						<Form.Label>{t("Upload File")}</Form.Label>
						<div className="custom-file-upload">
							<label className="file-label form-control">
								<input
									type="file"
									className="file-input"
									accept=".xlsx,.xls"
									onChange={handleFileChange}
									style={{ display: "none" }}
								/>
								<div className="file-name-container">
									<span className="wrap-file-name">
										{importFileName || t("No file chosen")}
									</span>
								</div>
								<span className="upload-btn">{t("Choose File")}</span>
							</label>
						</div>
						{importFileError && (
							<div className="text-danger mt-1">{importFileError}</div>
						)}
					</Form.Group>

					{selectedFile && (
						<div className="mt-3">
							<img
								src={excelDocumentImg}
								style={{ objectFit: "fill", borderRadius: "10px" }}
								height="65"
								width="65"
								alt={t("Excel file")}
							/>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={handleUpload}>
						{t("Import")}
					</Button>
					<Button variant="secondary" onClick={closeImportModal}>
						{t("Cancel")}
					</Button>
				</Modal.Footer>
			</Modal>

			<BarcodePrintModal
				show={Boolean(printBarcodeItem)}
				onHide={() => setPrintBarcodeItem(null)}
				product={printBarcodeItem}
			/>
		</Fragment>
	);
};

export default ItemsList;